import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { Row, Col, Card } from "react-bootstrap";
import moment from "moment";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.min.css";
import pako from "pako";
import DataTable from "./dataTable";
import { FilterByDropdownData } from "../helpers/dataFilterHelper";
import DropdownMenu from "./dropdownMenu";
import ChartLine from "./chartLine";
import "./chartPie.css";
import ChartPie from "./chartPie";
import "./dataComponent.css";
import {
  GetAllReportTypes,
  GetMRLCReportTypes,
  GetDownloadThemes,
  GetDownloadDatasets,
  GetBasemapThemes,
  GetBasemapInterfaceTypes,
  GetServiceThemes,
  GetServiceInterfaceTypes,
  GetViewerThemes,
  GetMRLCThemes,
  GetResponseCodes,
  GetLogSources,
  GetFileTypes,
  GetFileTypesByDownloadsTheme,
  GetFileTypesByDownloadsDataset,
} from "./dropdownConfig";
import { BaseUrl } from "../util/Api";
import Button from "react-bootstrap/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";

import downloadAliasConfig from "../alias/download_alias.json";
import basemapsAliasConfig from "../alias/basemaps_alias.json";
import servicesAliasConfig from "../alias/services_alias.json";
import viewerAliasConfig from "../alias/viewer_alias.json";

const DataComponent = () => {
  const [data, setData] = useState([]);
  const [aliasedData, setAliasedData] = useState([]);
  const [dataKeys, setDataKeys] = useState([]);

  const [noData, setNoData] = useState(false); // set to true if fetch returns nothing so component can display no results quickly
  const [isLoaded, setIsLoaded] = useState(false);
  const [showTop25USTopo, setShowTop25USTopo] = useState(false);
  const [showTop25LPC, setShowTop25LPC] = useState(false);
  const [showTop25MRLC, setShowTop25MRLC] = useState(false);
  const [setupNewData, setSetupNewData] = useState(null);
  const [runFilterDataByDates, setRunFilterDataByDates] = useState(false);

  const [sortDirection, setSortDirection] = useState("");
  const [sortField, setSortField] = useState("Hits");

  const [dynamicDropdownOptions, setDynamicDropdownOptions] = useState({
    dataset: [],
    interface: [],
    theme: GetDownloadThemes(),
  });
  const [selectedOptions, setSelectedOptions] = useState({
    type: "Summary by Theme",
    theme: "All",
    dataset: "All",
    fileType: "All",
    source: "All",
    interface: "",
    responseCode: "200 - OK",
    useInterfaceAlias: "True",
    useDatasetAlias: "True",
    useThemeAlias: "True",
  });

  const defaultSortDirection = {
    Description: "",
    Hits: "desc",
    Size: "desc",
    Interface: "",
  };
  const [dropdownOptions, setDropdownOptions] = useState({
    type: GetAllReportTypes(),
    source: GetLogSources(),
    fileTypes: GetFileTypes(),
    responseCode: GetResponseCodes(),
    useInterfaceAlias: ["True", "False"],
    useDatasetAlias: ["True", "False"],
    useThemeAlias: ["True", "False"],
    interface: [],
  });

  const startDate = useSelector((state) => state.startDate);
  const endDate = useSelector((state) => state.endDate);
  const activeRoute = useSelector((state) => state.activeRoute);

  const prevStartDate = useRef(startDate);
  const prevEndDate = useRef(endDate);
  const prevActiveRoute = useRef(activeRoute);

  // TODO: Refactor dataContainer so that it is the only part that rerenders when data changes

  const [showSpinner, setShowSpinner] = useState(true);
  const minimumSpinnerDuration = 200; // 0.2 seconds

  useEffect(() => {
    setShowSpinner(true);
    // Flag to track if the minimum duration has elapsed
    let minimumDurationElapsed = false;

    // Ensure that the spinner is shown for at least the minimum duration
    const minDurationTimer = setTimeout(() => {
      minimumDurationElapsed = true;
      if (isLoaded) {
        setShowSpinner(false);
      }
    }, minimumSpinnerDuration);

    // Hide the spinner when isLoaded becomes true after the minimum duration
    if (isLoaded && minimumDurationElapsed) {
      setShowSpinner(false);
      clearTimeout(minDurationTimer); // Clear the timer to prevent hiding the spinner prematurely
    }

    return () => {
      clearTimeout(minDurationTimer); // Cleanup the minimum duration timer on unmount or re-render
    };
  }, [isLoaded]);

  useEffect(() => {
    if (runFilterDataByDates) {
      filterDataByDates();
    }
  }, [runFilterDataByDates]);

  useEffect(() => {
    if (setupNewData === null) {
      return;
    }
    // The parameter tells setupData to retain current states/options
    setupData(setupNewData);
  }, [setupNewData]);

  useEffect(() => {
    if (noData === true || data.length > 0) {
      setIsLoaded(true);
    }
    setRunFilterDataByDates(false);
    setSetupNewData(null);
  }, [noData, data]);

  useEffect(() => {
    setNoData(false);
    setIsLoaded(false);
    const calendarDateFormat = "MM-DD-YYYY";

    // When dates change outside of current date range
    if (
      moment(startDate, calendarDateFormat).isBefore(
        moment(prevStartDate, calendarDateFormat)
      ) ||
      moment(endDate, calendarDateFormat).isAfter(
        moment(prevEndDate, calendarDateFormat)
      )
    ) {
      setData([]);
      setSortDirection(defaultSortDirection[sortField]);
      setupNewData(false);
    }

    // When dates change inside current date range
    if (
      moment(startDate, calendarDateFormat).isAfter(
        moment(prevStartDate, calendarDateFormat)
      ) ||
      moment(endDate, calendarDateFormat).isBefore(
        moment(prevEndDate, calendarDateFormat)
      )
    ) {
      setSortDirection(defaultSortDirection[sortField]);
      filterDataByDates();
    }

    if (activeRoute !== prevActiveRoute) {
      setIsLoaded(false);
      setSortDirection(defaultSortDirection[sortField]);
      setShowTop25USTopo(false);
      setShowTop25LPC(false);
      setShowTop25MRLC(false);
      setSetupNewData(true);
    }

    // Update previous values after each render
    prevStartDate.current = startDate;
    prevEndDate.current = endDate;
    prevActiveRoute.current = activeRoute;
  }, [startDate, endDate, activeRoute]);

  const getCategory = () => {
    const routeToCategory = {
      1: "downloads",
      2: "basemaps",
      3: "services",
      4: "viewer",
      5: "mrlc",
    };

    return routeToCategory[activeRoute] || "downloads"; // default category
  };

  const setupData = async (newData) => {
    setIsLoaded(false);
    setData([]);
    setDropdownOptions({ ...dropdownOptions, interface: [] });

    const jsonData = await fetchData();
    if (jsonData.length === 0) {
      setNoData(true);
      return;
    }
    let aliasData = [];
    // All selected options are set because we can't be sure which ones have changed during previous activeRoute
    switch (activeRoute) {
      case 1: {
        aliasData = alias(jsonData, downloadAliasConfig);
        setDropdownOptions({
          ...dropdownOptions,
          type: GetAllReportTypes(),
        });
        if (newData) {
          setDataKeys([
            "Description",
            "Dataset",
            "Type",
            "Hits",
            "Size",
            "Source",
            "Response",
          ]);
          setSelectedOptions({
            type: "Summary by Theme",
            theme: "All",
            dataset: "All",
            fileType: "All",
            source: "All",
            interface: "",
            responseCode: "200 - OK",
            useInterfaceAlias: "True",
            useDatasetAlias: "True",
            useThemeAlias: "True",
          });
          setDynamicDropdownOptions({
            ...dynamicDropdownOptions,
            theme: GetDownloadThemes(),
            dataset: GetDownloadDatasets(selectedOptions.theme),
          });
        }
        break;
      }
      case 2:
        aliasData = alias(jsonData, basemapsAliasConfig);
        setDropdownOptions({
          ...dropdownOptions,
          type: GetAllReportTypes(),
          responseCode: GetResponseCodes(),
        });
        setDataKeys(["Description", "Interface", "Hits", "Size"]);
        setSelectedOptions({
          type: "Summary by Theme",
          theme: "All",
          interface: "All",
          dataset: "",
          fileType: "All",
          source: "",
          responseCode: "All",
          useInterfaceAlias: "True",
          useDatasetAlias: "True",
          useThemeAlias: "True",
        });
        setDynamicDropdownOptions({
          ...dynamicDropdownOptions,
          theme: GetBasemapThemes(),
          interface: GetBasemapInterfaceTypes(),
        });

        break;
      case 3:
        aliasData = alias(jsonData, servicesAliasConfig);
        setDropdownOptions({
          ...dropdownOptions,
          type: GetAllReportTypes(),
          responseCode: GetResponseCodes(),
        });
        setDataKeys(["Description", "Interface", "Hits", "Size"]);
        setSelectedOptions({
          type: "Summary by Theme",
          theme: "All",
          interface: "All",
          dataset: "",
          fileType: "All",
          source: "",
          responseCode: "All",
          useInterfaceAlias: "True",
          useDatasetAlias: "True",
          useThemeAlias: "True",
        });
        setDynamicDropdownOptions({
          ...dynamicDropdownOptions,
          theme: GetServiceThemes(),
          interface: GetServiceInterfaceTypes(),
        });

        break;
      case 4:
        aliasData = alias(jsonData, viewerAliasConfig);
        setDropdownOptions({
          ...dropdownOptions,
          type: GetAllReportTypes(),
        });
        setDataKeys([
          "Description",
          "Hits",
          "Visitors",
          "Views",
          "Size",
          "Response",
        ]);
        setSelectedOptions({
          type: "Summary by Theme",
          theme: "All",
          interface: "",
          dataset: "",
          fileType: "All",
          source: "",
          responseCode: "All",
          useInterfaceAlias: "True",
          useDatasetAlias: "True",
          useThemeAlias: "True",
        });
        setDynamicDropdownOptions({
          ...dynamicDropdownOptions,
          theme: GetViewerThemes(),
        });
        break;
      case 5: {
        aliasData = jsonData;
        setDropdownOptions({
          ...dropdownOptions,
          type: GetMRLCReportTypes(),
        });
        if (newData) {
          setDataKeys(["Description", "Hits", "Size", "Response"]);
          setSelectedOptions({
            type: "Summary",
            theme: "All",
            dataset: "All",
            fileType: "All",
            source: "All",
            interface: "",
            responseCode: "200 - OK",
            useInterfaceAlias: "True",
            useDatasetAlias: "True",
            useThemeAlias: "True",
          });
          setDynamicDropdownOptions({
            ...dynamicDropdownOptions,
            theme: GetMRLCThemes(),
          });
        }
        break;
      }
      default:
        return;
    }
    setAliasedData(aliasData);
    setRunFilterDataByDates(true);
  };

  const alias = (json, aliasConfig) => {
    //BEGIN FILTERS
    const cleanUpValue = (value, toUpperCase) => {
      if (value) {
        value = value.replace("/", "");
        if (toUpperCase) {
          value = value.toUpperCase();
        }
      }
      return value;
    };

    const getAlias = (aliasValue) => (aliasValue ? aliasValue : "Unclassified");

    const aliasData = json.map((item) => {
      const themeKeyAlias = aliasConfig.theme[item.theme];
      item.theme_alias = getAlias(themeKeyAlias);

      //alias interface
      if (activeRoute === 2 || activeRoute === 3) {
        const cleanedUpValue = cleanUpValue(item.interface, true);
        const interFaceAlias = aliasConfig.interface[cleanedUpValue];
        item.interface_alias = getAlias(interFaceAlias);
      }

      //alias dataset
      if (activeRoute === 1) {
        const datasetValueFromJson = cleanUpValue(item.dataset, false);
        const datasetAliasGroup = aliasConfig.dataset[item.theme_alias] || {};

        let datasetAlias = datasetAliasGroup[datasetValueFromJson] || "";

        //This checks for aliases that start and end with % (ex: '%_20%': 'NAIP')
        //if they exists search for the value and alias it
        //(ex: ak_2020 and ak_2019 with get replaced with 'NAIP' because it contains _20)
        //this is supposed to mimic a %like%
        if (!datasetAlias) {
          for (const alias in datasetAliasGroup) {
            if (alias.startsWith("%") && alias.endsWith("%")) {
              if (datasetValueFromJson.includes(alias.replaceAll("%", ""))) {
                datasetAlias = datasetAliasGroup[alias];
                break;
              }
            }
          }
        }
        item.dataset_alias = getAlias(datasetAlias);
      }
      return item;
    });

    return aliasData;
  };

  const applyOtherFilters = (filteredbyDateData) => {
    let updatedDataKeys = [...dataKeys];

    const filteredData = FilterByDropdownData(
      filteredbyDateData,
      selectedOptions,
      activeRoute,
      showTop25USTopo,
      showTop25LPC,
      showTop25MRLC
    );

    if (selectedOptions.type === "Monthly") {
      if (!updatedDataKeys.includes("Average Daily Hits")) {
        updatedDataKeys.splice(
          updatedDataKeys.indexOf("Hits") + 1,
          0,
          "Average Daily Hits"
        );
      }
      if (!updatedDataKeys.includes("Average Daily Size")) {
        updatedDataKeys.splice(
          updatedDataKeys.indexOf("Size") + 1,
          0,
          "Average Daily Size"
        );
      }
    } else {
      if (updatedDataKeys.includes("Average Daily Hits")) {
        updatedDataKeys.splice(
          updatedDataKeys.indexOf("Average Daily Hits"),
          1
        );
      }
      if (updatedDataKeys.includes("Average Daily Size")) {
        updatedDataKeys.splice(
          updatedDataKeys.indexOf("Average Daily Size"),
          1
        );
      }
    }

    // If Report Type is Monthly or Daily, show Theme
    if (
      selectedOptions.type === "Monthly" ||
      selectedOptions.type === "Daily"
    ) {
      if (!updatedDataKeys.includes("Theme") && activeRoute !== 5) {
        updatedDataKeys.push("Theme");
      }
    } else {
      updatedDataKeys = updatedDataKeys.filter(
        (key) => key !== "Theme" && key !== "Theme"
      );
    }

    // If Dataset is USTopo, show state and map title
    if (selectedOptions.dataset === "US Topo" && showTop25USTopo) {
      if (!updatedDataKeys.includes("State", "Title")) {
        updatedDataKeys.push("State", "Title");
      }
      updatedDataKeys = updatedDataKeys.filter(
        (key) =>
          key !== "Type" &&
          key !== "Source" &&
          key !== "Theme" &&
          key !== "Average Daily Hits" &&
          key !== "Average Daily Size"
      );
    } else {
      updatedDataKeys = updatedDataKeys.filter(
        (key) => key !== "State" && key !== "Title"
      );
    }

    // If Dataset is LPC, show file name
    if (selectedOptions.dataset === "LPC" && showTop25LPC) {
      if (!updatedDataKeys.includes("Name")) {
        updatedDataKeys.push("Name");
      }
      updatedDataKeys = updatedDataKeys.filter(
        (key) =>
          key !== "Type" &&
          key !== "Source" &&
          key !== "Theme" &&
          key !== "Average Daily Hits" &&
          key !== "Average Daily Size"
      );
    } else {
      updatedDataKeys = updatedDataKeys.filter((key) => key !== "Name");
    }

    // If Downloads but not top USTopo or LPC, reset data keys
    if (!showTop25USTopo && !showTop25LPC && activeRoute === 1) {
      if (!updatedDataKeys.includes("Type", "Source")) {
        updatedDataKeys.push("Type", "Source");
      }
    }

    // If MRLC and showTop25, show map title
    if (activeRoute === 5 && showTop25MRLC) {
      if (!updatedDataKeys.includes("Title")) {
        updatedDataKeys.push("Title");
      }
      updatedDataKeys = updatedDataKeys.filter(
        (key) =>
          key !== "Type" &&
          key !== "Source" &&
          key !== "Theme" &&
          key !== "Average Daily Hits" &&
          key !== "Average Daily Size"
      );
    }

    setDataKeys(updatedDataKeys);
    return filteredData;
  };

  const changeSortOrder = (fieldName) => {
    if (sortField !== fieldName) {
      setSortField(fieldName);
      setSortDirection(defaultSortDirection[fieldName]);
    } else {
      const direction = sortDirection === "" ? "desc" : "";
      setSortDirection(direction);
    }
  };

  const fetchData = async () => {
    const inputDateFormat = "YYYYMMDD";
    const formattedStartDate = moment(startDate).format(inputDateFormat);
    const formattedEndDate = moment(endDate).format(inputDateFormat);
    const category = getCategory();
    const allRecords = [];

    try {
      const fetchDataBatch = async (url, nextPageKey) => {
        const response = await fetch(
          nextPageKey ? `${url}&nextPageKey=${nextPageKey}` : url
        );

        if (![200, 201].includes(response.status)) {
          return null;
        }

        const result = await response.json();
        const compressedData = result?.data.data;
        const decompressedData = pako.inflate(new Uint8Array(compressedData), {
          to: "string",
        });
        const jsonData = JSON.parse(decompressedData);

        for (const day of jsonData) {
          if (Array.isArray(day.records)) {
            allRecords.push(...day.records);
          }
        }

        return result.nextPageKey || null;
      };

      const runQueryForCategory = async (cat) => {
        let nextPageKey = null;
        const baseUrl = BaseUrl(cat, formattedStartDate, formattedEndDate);

        do {
          nextPageKey = await fetchDataBatch(baseUrl, nextPageKey);
        } while (nextPageKey !== null);
      };

      if (
        (selectedOptions.dataset === "US Topo" && showTop25USTopo) ||
        (selectedOptions.dataset === "LPC" && showTop25LPC)
      ) {
        await runQueryForCategory(
          selectedOptions.dataset.toLowerCase().replace(/\s+/g, "")
        );
      } else {
        await runQueryForCategory(category);
      }

      return allRecords;
    } catch (error) {
      toast.error(
        "Internal Server Error. Please try again later.",
        toastErrorProperties
      );
      console.error("Error fetching data:", error);
      setIsLoaded(false);
      setShowSpinner(false);
      return [];
    }
  };

  const filterDataByDates = async () => {
    const dataFiltered = applyOtherFilters(aliasedData);
    setDynamicDropdownOptions({
      type: updateTypesDropdown(),
      theme: updateThemesDropdown(),
      dataset: updateDatasetsDropdown(),
      interface: updateInterfaceDropdown(),
    });
    dataFiltered.length > 0 ? setData(dataFiltered) : setNoData(true);
  };

  const selectOption = async (name, selected) => {
    setIsLoaded(false);
    setData([]);
    const allThemes = "All";
    const allDatasets = "All";
    const allInterfaces = "All";

    const setOptions = async () => {
      setSelectedOptions({
        ...selectedOptions,
        [name]: selected,
      });
      return true;
    };

    const updateOptions = (theme, dataset) => {
      setSelectedOptions({
        ...selectedOptions,
        responseCode: "200 - OK",
        theme: theme,
        dataset: dataset,
        [name]: selected,
      });
    };

    const hideTopSetSort = () => {
      setShowTop25USTopo(false);
      setShowTop25LPC(false);
      setShowTop25MRLC(false);
      setSortField("Hits");
      setSortDirection(defaultSortDirection["Hits"]);
    };

    switch (name) {
      case "type":
        if (selected === "Daily" || selected === "Monthly") {
          setSortField("Description");
          setSortDirection(defaultSortDirection["Description"]);

          if (showTop25USTopo) {
            setShowTop25USTopo(false);
            updateOptions("Maps", "US Topo");
            setupData(true);
          } else if (showTop25LPC) {
            setShowTop25LPC(false);
            updateOptions("Elevation", "LPC");
            setupData(true);
          } else if (showTop25MRLC) {
            setShowTop25MRLC(false);
            updateOptions("", "");
            setupData(true);
          } else {
            setOptions();
            setRunFilterDataByDates(true);
          }
        }
        if (selected === "Summary" || selected === "Summary by Theme") {
          if (showTop25USTopo || showTop25LPC || showTop25MRLC) {
            setOptions();
            hideTopSetSort(() => setupData(true));
          } else {
            setOptions();
            setRunFilterDataByDates(true);
          }
        }
        break;
      case "useThemeAlias":
        if (showTop25USTopo || showTop25LPC) {
          hideTopSetSort(() => {
            updateOptions(allThemes, allDatasets);
            setupData(true);
          });
        } else {
          setSelectedOptions({
            ...selectedOptions,
            theme: allThemes,
            dataset: allDatasets,
            [name]: selected,
          });
          setRunFilterDataByDates(true);
        }
        break;

      case "useDatasetAlias":
      case "useInterfaceAlias":
        if (showTop25USTopo || showTop25LPC) {
          hideTopSetSort();

          if (showTop25USTopo) {
            updateOptions("Maps", allDatasets);
            setSetupNewData(false);
          }
          if (showTop25LPC) {
            updateOptions("Elevation", allDatasets);
            setSetupNewData(false);
          }
        } else {
          setSelectedOptions({
            ...selectedOptions,
            dataset: allDatasets,
            interface: allInterfaces,
            [name]: selected,
          });
          setRunFilterDataByDates(true);
        }
        break;
      case "source": {
        let selectedTheme = "";
        if (showTop25USTopo) {
          selectedTheme = "Maps";
        } else if (showTop25LPC) {
          selectedTheme = "Elevation";
        } else {
          setSelectedOptions({
            ...selectedOptions,
            dataset: allDatasets,
            [name]: selected,
          });
          setRunFilterDataByDates(true);
          return;
        }
        // This block runs if showTop25USTopo or showTop25LPC
        hideTopSetSort(() => updateOptions(selectedTheme, allDatasets));
        setSetupNewData(false);
        break;
      }
      case "theme":
        if (showTop25USTopo || showTop25LPC) {
          hideTopSetSort(() => updateOptions(selected, allDatasets));
          setSetupNewData(false);
        } else {
          if (activeRoute === 1) {
            setDropdownOptions({
              ...dropdownOptions,
              fileTypes: GetFileTypesByDownloadsTheme(selected),
            });
          }
          setSelectedOptions({
            ...selectedOptions,
            [name]: selected,
            dataset: allDatasets,
          });
          setRunFilterDataByDates(true);
        }
        break;
      case "dataset":
        if (showTop25USTopo || showTop25LPC) {
          hideTopSetSort(() => updateOptions(selectedOptions.theme, selected));
          setSetupNewData(false);
        } else {
          if (activeRoute === 1) {
            if (selected === "All") {
              setDropdownOptions({
                ...dropdownOptions,
                fileTypes: GetFileTypesByDownloadsTheme(selectedOptions.theme),
              });
            } else {
              setDropdownOptions({
                ...dropdownOptions,
                fileTypes: GetFileTypesByDownloadsDataset(
                  selectedOptions.theme,
                  selected
                ),
              });
            }
          }
          setOptions();
          setRunFilterDataByDates(true);
        }
        break;
      default:
        setOptions();
        setRunFilterDataByDates(true);
        break;
    }
  };

  const showHideTop25Handler = async (
    showTop25USTopo,
    showTop25LPC,
    showTop25MRLC,
    theme,
    dataset
  ) => {
    await updateTopStates(
      showTop25USTopo,
      showTop25LPC,
      showTop25MRLC,
      theme,
      dataset
    );
    setSetupNewData(false);
  };

  const showHideTop25LPCHandler = async () => {
    const theme = "Elevation";
    const dataset = "LPC";
    showHideTop25Handler(
      showTop25USTopo,
      !showTop25LPC,
      showTop25MRLC,
      theme,
      dataset
    );
  };

  const showHideTop25USTopoHandler = async () => {
    const theme = "Maps";
    const dataset = "US Topo";
    showHideTop25Handler(
      !showTop25USTopo,
      showTop25LPC,
      showTop25MRLC,
      theme,
      dataset
    );
  };

  const showHideTop25MRLCHandler = async () => {
    const theme = "All";
    const dataset = "";
    showHideTop25Handler(
      showTop25USTopo,
      showTop25LPC,
      !showTop25MRLC,
      theme,
      dataset
    );
  };

  const toastErrorProperties = {
    position: "top-center",
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: "colored",
  };

  const updateDatasetsDropdown = () => {
    if (
      selectedOptions.useDatasetAlias === "True" ||
      selectedOptions.useDatasetAlias === "False"
    ) {
      return GetDownloadDatasets(selectedOptions.theme);
    } else if (selectedOptions.dataset === "All") {
      let unique;
      const aliasArray = data.map((item) => {
        return item["Dataset"];
      });
      unique = [...new Set(aliasArray.filter((item) => item !== ""))];
      unique = unique.sort();
      if (unique[0] !== "All") unique.unshift("All");
      return unique;
    } else {
      return dynamicDropdownOptions.dataset;
    }
  };

  const updateInterfaceDropdown = () => {
    if (selectedOptions.useInterfaceAlias === "True") {
      switch (activeRoute) {
        case 2:
          return GetBasemapInterfaceTypes();
        case 3:
          return GetServiceInterfaceTypes();
        default:
          return [];
      }
    } else if (selectedOptions.interface === "All") {
      let unique;
      const aliasArray = data.map((item) => {
        return item["Interface"];
      });
      unique = [...new Set(aliasArray.filter((item) => item !== ""))];
      unique = unique.sort();
      if (unique[0] !== "All") unique.unshift("All");
      return unique;
    } else {
      return dynamicDropdownOptions.interface;
    }
  };

  const updateTypesDropdown = () => {
    if (activeRoute === 5) {
      return GetMRLCReportTypes();
    } else return GetAllReportTypes();
  };

  const updateThemesDropdown = () => {
    let unique;
    if (
      selectedOptions.useThemeAlias === "True" ||
      selectedOptions.useThemeAlias === "False"
    ) {
      switch (activeRoute) {
        case 1:
          return GetDownloadThemes();
        case 2:
          return GetBasemapThemes();
        case 3:
          return GetServiceThemes();
        case 4:
          return GetViewerThemes();
        case 5:
          return GetMRLCThemes();
        default:
          return;
      }
    } else if (selectedOptions.theme === "All") {
      const aliasArray = data.map((item) => {
        return item["Description"];
      });
      unique = [...new Set(aliasArray)];
      unique = unique.sort();
      if (unique[0] !== "All") unique.unshift("All");
      return unique;
    } else {
      return dynamicDropdownOptions.theme;
    }
  };

  const updateTopStates = async (
    showTop25USTopo,
    showTop25LPC,
    showTop25MRLC,
    theme,
    dataset
  ) => {
    setShowTop25USTopo(showTop25USTopo);
    setShowTop25LPC(showTop25LPC);
    setShowTop25MRLC(showTop25MRLC);
    setIsLoaded(false);
    setData([]);
    setSelectedOptions({
      ...selectedOptions,
      responseCode: "200 - OK",
      source: "All",
      theme: theme,
      dataset: dataset,
    });

    setSortField("Hits");
    setSortDirection(defaultSortDirection["Hits"]);
  };

  const dataContainer = (
    <div>
      <ToastContainer />
      <Row className="row-report">
        <Col className="col-form" sm={6} md={4} lg={2}>
          <DropdownMenu
            title="Report Type Dropdown"
            dropdownLabel="Report Type"
            dropdownName="type"
            dropdownOptions={dropdownOptions.type}
            selectedOption={selectedOptions.type}
            selectOption={(selected) => selectOption("type", selected)}
          />
        </Col>
        {activeRoute !== 5 ? (
          <Col className="col-form" sm={6} md={4} lg={2}>
            <DropdownMenu
              title="Theme Dropdown"
              dropdownLabel="Themes"
              dropdownName="theme"
              dropdownOptions={dynamicDropdownOptions.theme}
              selectedOption={selectedOptions.theme}
              selectOption={(selected) => selectOption("theme", selected)}
            />
          </Col>
        ) : null}
        {activeRoute === 2 || activeRoute === 3 ? (
          <Col className="col-form" sm={6} md={4} lg={2}>
            <DropdownMenu
              title="Interface Dropdown"
              dropdownLabel="Interface"
              dropdownName="interface"
              dropdownOptions={dynamicDropdownOptions.interface}
              selectedOption={selectedOptions.interface}
              selectOption={(selected) => selectOption("interface", selected)}
            />
          </Col>
        ) : null}
        {activeRoute === 1 ? (
          <Col className="col-form" sm={6} md={4} lg={2}>
            <DropdownMenu
              title="Dataset Dropdown"
              dropdownLabel="Dataset"
              dropdownName="dataset"
              dropdownOptions={dynamicDropdownOptions.dataset}
              selectedOption={selectedOptions.dataset}
              selectOption={(selected) => selectOption("dataset", selected)}
            />
          </Col>
        ) : null}
        {activeRoute === 1 ? (
          <Col className="col-form" sm={6} md={4} lg={2}>
            <DropdownMenu
              title="Log Source Dropdown"
              dropdownLabel="Log Source"
              dropdownName="source"
              dropdownOptions={dropdownOptions.source}
              selectedOption={selectedOptions.source}
              selectOption={(selected) => selectOption("source", selected)}
            />
          </Col>
        ) : null}
        {activeRoute === 1 ? (
          <Col className="col-form" sm={6} md={4} lg={2}>
            <DropdownMenu
              title="File Dropdown"
              dropdownLabel="File Type"
              dropdownName="fileType"
              dropdownOptions={dropdownOptions.fileTypes}
              selectedOption={selectedOptions.fileType}
              selectOption={(selected) => selectOption("fileType", selected)}
            />
          </Col>
        ) : null}
        {activeRoute === 1 || activeRoute === 5 ? (
          <Col className="col-form" sm={6} md={4} lg={2}>
            <DropdownMenu
              title="Response Dropdown"
              dropdownLabel="Response"
              dropdownName="responseCode"
              dropdownOptions={dropdownOptions.responseCode}
              selectedOption={selectedOptions.responseCode}
              selectOption={(selected) =>
                selectOption("responseCode", selected)
              }
            />
          </Col>
        ) : null}
        {activeRoute === 5 ? (
          <Col className="col-form" sm={6} md={4} lg={2}>
            <Button
              className="top25-btn"
              variant="info"
              onClick={() => showHideTop25MRLCHandler()}
            >
              {showTop25MRLC ? "Hide Top 25" : "Show Top 25"}
            </Button>
          </Col>
        ) : null}
      </Row>
      <Row className="row-alias">
        {activeRoute !== 5 ? (
          <Col className="col-form" sm={6} md={4} lg={2}>
            <DropdownMenu
              title="Alias Theme Dropdown"
              dropdownLabel="Alias Theme"
              dropdownName="useThemeAlias"
              dropdownOptions={dropdownOptions.useThemeAlias}
              selectedOption={selectedOptions.useThemeAlias}
              selectOption={(selected) =>
                selectOption("useThemeAlias", selected)
              }
            />
          </Col>
        ) : null}
        {activeRoute === 1 ? (
          <Col className="col-form" sm={6} md={4} lg={2}>
            <DropdownMenu
              title="Alias Dataset Dropdown"
              dropdownLabel="Alias Dataset"
              dropdownName="useDatasetAlias"
              dropdownOptions={dropdownOptions.useDatasetAlias}
              selectedOption={selectedOptions.useDatasetAlias}
              selectOption={(selected) =>
                selectOption("useDatasetAlias", selected)
              }
            />
          </Col>
        ) : null}
        {activeRoute === 2 || activeRoute === 3 ? (
          <Col className="col-form" sm={6} md={4} lg={2}>
            <DropdownMenu
              title="Alias Interface Dropdown"
              dropdownLabel="Alias Interface"
              dropdownName="useInterfaceAlias"
              dropdownOptions={dropdownOptions.useInterfaceAlias}
              selectedOption={selectedOptions.useInterfaceAlias}
              selectOption={(selected) =>
                selectOption("useInterfaceAlias", selected)
              }
            />
          </Col>
        ) : null}
        {selectedOptions.dataset === "US Topo" ? (
          <Col className="col-form" sm={6} md={4} lg={2}>
            <Button
              className="top25-btn"
              variant="info"
              onClick={() => showHideTop25USTopoHandler()}
            >
              {showTop25USTopo ? "Hide Top 25" : "Show Top 25"}
            </Button>
          </Col>
        ) : null}
        {selectedOptions.dataset === "LPC" ? (
          <Col className="col-form" sm={6} md={4} lg={2}>
            <Button
              className="top25-btn"
              variant="info"
              onClick={() => showHideTop25LPCHandler()}
            >
              {showTop25LPC ? "Hide Top 25" : "Show Top 25"}
            </Button>
          </Col>
        ) : null}
      </Row>
      {isLoaded && data && data.length > 0 ? (
        <Card header="Data">
          <DataTable
            style={{ padding: 10 }}
            data={data}
            sortField={sortField}
            sortDirection={sortDirection}
            dataKeys={dataKeys}
            changeSortOrder={(fieldName) => changeSortOrder(fieldName)}
          />
        </Card>
      ) : null}

      {isLoaded && noData ? (
        <div style={{ textAlign: "center", paddingTop: 20 }}>
          There are no results for your selections.
        </div>
      ) : null}

      {selectedOptions.type === "Daily" ||
      selectedOptions.type === "Monthly" ? (
        <div>
          <ChartLine allData={data} chartType={selectedOptions.type} />
        </div>
      ) : null}

      {isLoaded &&
      selectedOptions.type === "Summary by Theme" &&
      showTop25USTopo === false &&
      showTop25LPC === false &&
      showTop25MRLC === false &&
      (selectedOptions.dataset === "" || selectedOptions.dataset === "All") &&
      (selectedOptions.interface === "" ||
        selectedOptions.interface === "All") ? (
        <div>
          <ChartPie allData={data} />
        </div>
      ) : null}
    </div>
  );

  return (
    <div>
      {dataContainer}
      {showSpinner && (
        <div id="splash" className="show-spinner">
          <FontAwesomeIcon icon={faSpinner} spin />
        </div>
      )}
    </div>
  );
};

export default DataComponent;
